import TransferFallback from '../../Components/TransferFallback';
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { filesize } from 'filesize';
import CommonUtils from '../../Utils/commonUtils';
import { isRelayOnlyRequested } from '../../Utils/relayOnly';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import LinearProgress from '@mui/material/LinearProgress';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import CancelIcon from '@mui/icons-material/Cancel';
import ReplayIcon from '@mui/icons-material/Replay';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import VideocamIcon from '@mui/icons-material/Videocam';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import HttpsIcon from '@mui/icons-material/Https';
import LockIcon from '@mui/icons-material/Lock';
import SendIcon from '@mui/icons-material/Send';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import {
    convertToJpeg,
    isHeicFile,
    isImageFile,
    isPdfFile,
    isVideoFile,
    isAudioFile,
    getVideoThumbnail,
    createThumbnail,
} from '../../Utils/heicConverter';
import { FileReceiver, FileMetadata } from '../../Services/FileReceiver';
import { Signaling } from '../../Services/Signaling';

import firebaseService from '../../Services/firebaseService';
import { log, error } from '../../Utils/logger';
import { StartConfig } from '../../StartConfig';
import SEO from '../../Components/SEO';
import { UtmUtils } from '../../Utils/UtmTracker';
import LegalDialog from '../../Components/LegalDialog';
import FeedbackSection from '../../Components/FeedbackSection';
import Header from '../../Components/Header';
import { useI18n } from '../../i18n/I18nProvider';
import { useLocation, useNavigate } from 'react-router-dom';
import { SUPPORT_MAILTO } from '../../Config/support';

const QR_CODE_SIZE = 210;
const QR_LOGO_SRC = '/logo192.png?v=3';
const HEADER_SCROLL_GAP_PX = 12;

function PulsingTitle() {
    const [cycle, setCycle] = useState(0);

    useEffect(() => {
        let repeatInterval = 4000;
        let completedSequences = 0;
        let timeout: ReturnType<typeof setTimeout>;
        const pulse = () => {
            setCycle((current) => current + 1);
            completedSequences += 1;
            // Play the initial sequence plus ten repeats.
            if (completedSequences <= 10) {
                timeout = setTimeout(pulse, repeatInterval);
                repeatInterval += 1000;
            }
        };
        timeout = setTimeout(pulse, 5000);
        return () => clearTimeout(timeout);
    }, []);

    return (
        <React.Fragment key={cycle}>
            <span
                className="fileplease-title-file"
                style={{ animationName: cycle === 0 ? 'none' : undefined }}
            >
                File
            </span>
            <span
                className="fileplease-title-please"
                style={{
                    color: '#6D28D9',
                    animationName: cycle === 0 ? 'none' : undefined,
                }}
            >
                Please
            </span>
        </React.Fragment>
    );
}

let qrCodeStylingModulePromise: Promise<any> | null = null;

function loadQRCodeStylingModule() {
    if (!qrCodeStylingModulePromise) {
        qrCodeStylingModulePromise = import('qr-code-styling');
    }
    return qrCodeStylingModulePromise;
}

function StyledQRCode({ value }: { value: string }) {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const qrCodeRef = useRef<any>(null);
    const [isReady, setIsReady] = useState(false);

    useEffect(() => {
        let isMounted = true;

        const render = async () => {
            const { default: QRCodeStyling } = await loadQRCodeStylingModule();
            if (!isMounted || !containerRef.current) return;

            if (!qrCodeRef.current) {
                qrCodeRef.current = new QRCodeStyling({
                    width: QR_CODE_SIZE,
                    height: QR_CODE_SIZE,
                    type: 'svg',
                    data: value,
                    image: QR_LOGO_SRC,
                    // Keep a white quiet zone around the code for scan reliability.
                    margin: 12,
                    qrOptions: {
                        errorCorrectionLevel: 'H',
                    },
                    dotsOptions: {
                        color: '#000000',
                        type: 'rounded',
                    },
                    cornersSquareOptions: {
                        color: '#000000',
                        type: 'extra-rounded',
                    },
                    cornersDotOptions: {
                        color: '#000000',
                        type: 'dot',
                    },
                    backgroundOptions: {
                        color: '#ffffff',
                    },
                    imageOptions: {
                        crossOrigin: 'anonymous',
                        hideBackgroundDots: true,
                        imageSize: 0.2,
                        margin: 2,
                    },
                });
                containerRef.current.innerHTML = '';
                qrCodeRef.current.append(containerRef.current);
            } else {
                qrCodeRef.current.update({
                    data: value,
                    image: QR_LOGO_SRC,
                });
            }

            if (isMounted) {
                setIsReady(true);
            }
        };

        void render().catch((err) => {
            error('Failed to render styled QR code', err);
        });

        return () => {
            isMounted = false;
        };
    }, [value]);

    useEffect(() => {
        const containerEl = containerRef.current;
        return () => {
            qrCodeRef.current = null;
            if (containerEl) {
                containerEl.innerHTML = '';
            }
        };
    }, []);

    return (
        <div className="my-[20px] min-h-[210px] flex items-center justify-center">
            {!isReady && <CircularProgress size={40} />}
            <div
                className="bg-white rounded-lg"
                style={{ display: isReady ? 'block' : 'none' }}
            >
                <div ref={containerRef} />
            </div>
        </div>
    );
}

function renderQRCode(url: string) {
    return <StyledQRCode value={url} />;
}

type StartCtaLinkOptions = {
    ctaLabel: string;
    onCtaClick: () => void;
    ctaDisabled?: boolean;
};

function renderStartCtaLink(options?: StartCtaLinkOptions, marginTop = 1.5) {
    if (!options) {
        return null;
    }

    return (
        <Button
            variant="text"
            onClick={options.onCtaClick}
            disabled={options.ctaDisabled}
            sx={{
                mt: marginTop,
                px: 0,
                minWidth: 'auto',
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '1rem',
                color: '#2563EB',
                textDecoration: 'underline',
                textUnderlineOffset: '3px',
                alignSelf: 'flex-start',
                '&:hover': {
                    backgroundColor: 'transparent',
                    textDecoration: 'underline',
                },
            }}
        >
            {options.ctaLabel}
        </Button>
    );
}

function scrollToElementRespectingHeader(
    element: HTMLElement,
    behavior: ScrollBehavior = 'smooth',
) {
    const appBar =
        document.querySelector<HTMLElement>('.MuiAppBar-root') ??
        document.querySelector<HTMLElement>('header');
    const headerHeight = appBar?.getBoundingClientRect().height ?? 64;
    const targetTop =
        window.scrollY +
        element.getBoundingClientRect().top -
        headerHeight -
        HEADER_SCROLL_GAP_PX;

    window.scrollTo({
        top: Math.max(0, targetTop),
        behavior,
    });
}

function scrollPageToTop() {
    window.scrollTo({
        top: 0,
        behavior: 'auto',
    });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
}

function createFileReceiver(
    signaling: Signaling,
    key: CryptoKey,
    onReceivedFile: (id: number, file: File) => void,
    setIsConnected: (connected: boolean) => void,
    isConnectedRef: React.MutableRefObject<boolean>,
    setIsConnecting: (connecting: boolean) => void,
    setReceivingFiles: React.Dispatch<
        React.SetStateAction<Record<number, ReceivingFile>>
    >,
    sessionId: string,
    onConnectionStateChange?: (
        state: RTCPeerConnectionState,
        receiver: FileReceiver,
    ) => void,
): FileReceiver {
    const fileReceiver = new FileReceiver(signaling, key);
    fileReceiver.setCallbacks(
        (id, progress, bytesReceived) => {
            const now = Date.now();
            setReceivingFiles((prev) => {
                const file = prev[id];
                if (!file) return prev;
                const elapsed = now - file.startTime;
                const speed = CommonUtils.formatSpeed(bytesReceived, elapsed);
                return {
                    ...prev,
                    [id]: {
                        ...file,
                        progress,
                        currentBytes: bytesReceived,
                        speed,
                    },
                };
            });
        },
        async (id, file) => {
            log('File received:', file, 'ID:', id);
            onReceivedFile(id, file);
            let transferDuration = 0;
            setReceivingFiles((prev) => {
                const receivingFile = prev[id];
                if (receivingFile) {
                    transferDuration = Date.now() - receivingFile.startTime;
                }
                const next = { ...prev };
                delete next[id];
                return next;
            });

            const connectionType = await fileReceiver.getConnectionType();
            firebaseService.logEvent('file_received_success', {
                fileExtension: file.name.split('.').pop(),
                fileSize: file.size,
                fileType: file.type,
                connectionType,
                transferDuration,
            });

            firebaseService.logFileTransfer({
                ext: file.name.split('.').pop() || '',
                is_sender: true, // true for Start.tsx
                is_sent: false, // false for received
                session_id: sessionId,
                size: file.size,
                time_to_send_ms: transferDuration,
                transport: connectionType === 'server' ? 'server' : 'p2p',
            });
            firebaseService.logFirstFileTransfered(
                sessionId,
                file.name.split('.').pop() || '',
                file.size,
                transferDuration,
                connectionType === 'server',
            );

            const blob = file instanceof Blob ? file : new Blob([file]);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = file.name;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);

            setTimeout(() => {
                URL.revokeObjectURL(url);
            }, 1000);
        },
        (state) => {
            log('Connection state changed:', state);
            const connected = state === 'connected';
            setIsConnected(connected);
            isConnectedRef.current = connected;
            if (connected) {
                setIsConnecting(false);
            } else if (state === 'connecting') {
                setIsConnecting(true);
            }
            onConnectionStateChange?.(state, fileReceiver);
        },
        (id, metadata: FileMetadata) => {
            setReceivingFiles((prev) => ({
                ...prev,
                [id]: {
                    id,
                    name: metadata.name,
                    size: metadata.size,
                    mimeType: metadata.mimeType,
                    uploadPending: metadata.uploadPending,
                    progress: 0,
                    currentBytes: 0,
                    startTime: prev[id]?.startTime ?? Date.now(),
                    speed: '0 B/s',
                },
            }));
        },
        async (id, err: Error) => {
            error('FileReceiver error for ID:', id, err);
            const connectionType = await fileReceiver.getConnectionType();
            firebaseService.logEvent('file_receive_failure', {
                error: err.message,
                transferId: id,
                connectionType,
            });
            if (id !== null) {
                setReceivingFiles((prev) => {
                    const next = { ...prev };
                    delete next[id];
                    return next;
                });
            }
        },
    );
    void signaling
        .ensureSessionCreated()
        .then(() => fileReceiver.createOffer())
        .catch((errValue) => {
            const message =
                errValue instanceof Error
                    ? errValue.message
                    : String(errValue ?? 'unknown');
            error('FileReceiver: Failed to create offer', errValue);
            signaling.suggestServer('offer_create_failed');
            void firebaseService.logEvent('offer_create_failed', {
                session_id: sessionId,
                message,
            });
        });
    return fileReceiver;
}

type ReceivingFile = {
    uploadPending?: boolean;
    id: number;
    name: string;
    size: number;
    mimeType: string;
    progress: number;
    currentBytes: number;
    startTime: number;
    speed: string;
};

type SendFileProps = {
    id: string;
    file: File | null;
    name: string;
    size: number;
};

function SendFileItem({
    file,
    progress,
    errorText,
    onPreview,
    onRetry,
    speed,
    isConnected,
    isWaiting,
    name,
    size,
    onCancel,
}: {
    file: File | null;
    progress: number;
    errorText?: string | null;
    onPreview: (file: File) => void;
    onRetry?: () => void;
    speed?: string;
    isConnected?: boolean;
    isWaiting?: boolean;
    name: string;
    size: number;
    onCancel?: () => void;
}) {
    const { t } = useI18n();
    const isCancelled = errorText === 'cancelled';
    return (
        <Card variant="outlined" className="mb-[10px]">
            <CardContent className="flex items-center">
                <div className="mr-4 flex-shrink-0">
                    <InsertDriveFileIcon
                        style={{ fontSize: 40, color: '#9e9e9e' }}
                    />
                </div>
                <div className="flex-grow min-w-0">
                    <Typography
                        variant="body1"
                        noWrap
                        className="font-medium text-left"
                    >
                        {name}{' '}
                        <span className="text-gray-600 font-normal text-sm ml-1">
                            ({filesize(size)})
                        </span>
                    </Typography>
                    <div className="mt-2 w-full">
                        <LinearProgress
                            variant="determinate"
                            value={progress}
                            sx={{
                                height: 6,
                                borderRadius: 3,
                                backgroundColor:
                                    isCancelled || errorText
                                        ? '#ffebee'
                                        : undefined,
                                '& .MuiLinearProgress-bar': {
                                    backgroundColor:
                                        isCancelled || errorText
                                            ? '#ef5350'
                                            : undefined,
                                },
                            }}
                        />
                        <div className="flex justify-between items-center mt-0.5">
                            {errorText ? (
                                <Typography
                                    variant="caption"
                                    className="text-red-500 font-medium truncate flex-grow mr-2"
                                >
                                    {t('status.errorPrefix')} {errorText}
                                </Typography>
                            ) : speed && progress > 0 && progress < 100 ? (
                                <Typography
                                    variant="caption"
                                    className="text-blue-500 font-mono font-bold"
                                >
                                    {speed}
                                </Typography>
                            ) : progress === 0 && !errorText && !isConnected ? (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: '#2e7d32',
                                        fontWeight: 'medium',
                                    }}
                                >
                                    {t('status.waiting')}
                                </Typography>
                            ) : (
                                <div />
                            )}
                            <Typography
                                variant="caption"
                                className={`block text-right ${isCancelled || errorText ? 'text-red-600 font-bold' : 'text-gray-700'}`}
                            >
                                {isCancelled
                                    ? t('status.cancelled')
                                    : errorText
                                        ? t('status.failed')
                                        : progress === 100
                                            ? t('status.sent')
                                            : isWaiting
                                                ? t('status.waiting')
                                                : `${progress}%`}
                            </Typography>
                        </div>
                    </div>
                </div>
                {progress > 0 && progress < 100 && !errorText && onCancel && (
                    <div className="ml-4 flex flex-col items-center">
                        <IconButton
                            onClick={(e) => {
                                e.stopPropagation();
                                onCancel();
                            }}
                            sx={{ color: '#9e9e9e', p: 0.5 }}
                            size="small"
                        >
                            <CancelIcon fontSize="small" />
                        </IconButton>
                        <Typography
                            variant="caption"
                            sx={{
                                color: '#9e9e9e',
                                fontSize: '0.65rem',
                                mt: -0.5,
                            }}
                        >
                            {t('action.cancel')}
                        </Typography>
                    </div>
                )}
                {errorText && errorText !== 'cancelled' && onRetry && (
                    <div className="ml-4">
                        <IconButton
                            onClick={onRetry}
                            className="text-blue-500 hover:text-blue-700 bg-blue-50 p-1"
                            size="small"
                        >
                            <ReplayIcon fontSize="small" />
                        </IconButton>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

function renderSendFiles(
    files: SendFileProps[],
    sendProgress: Record<string, number>,
    sendErrors: Record<string, string | null>,
    onPreview: (file: File) => void,
    onRetry: (file: File, id: string) => void,
    sendSpeeds: Record<string, string>,
    isConnected: boolean,
    currentlySendingId: string | null,
    onCancel: (id: string) => void,
) {
    if (files.length === 0) return null;
    return (
        <div className="animate-fade-in-up">
            {files.map((fileItem) => (
                <SendFileItem
                    key={fileItem.id}
                    file={fileItem.file}
                    name={fileItem.name}
                    size={fileItem.size}
                    progress={sendProgress[fileItem.id] || 0}
                    errorText={sendErrors[fileItem.id] || null}
                    onPreview={onPreview}
                    onRetry={() =>
                        fileItem.file && onRetry(fileItem.file, fileItem.id)
                    }
                    onCancel={() => onCancel(fileItem.id)}
                    speed={sendSpeeds[fileItem.id]}
                    isConnected={isConnected}
                    isWaiting={
                        (sendProgress[fileItem.id] || 0) < 100 &&
                        !sendErrors[fileItem.id] &&
                        currentlySendingId !== fileItem.id
                    }
                />
            ))}
        </div>
    );
}

function ImagePreviewModal({
    file,
    onClose,
    previewUrl,
}: {
    file: File | null;
    onClose: () => void;
    previewUrl?: string | null;
}) {
    const [imageSrc, setImageSrc] = useState<string>('');
    const [pdfUrl, setPdfUrl] = useState<string>('');
    const [videoUrl, setVideoUrl] = useState<string>('');
    const [audioUrl, setAudioUrl] = useState<string>('');
    const [isConverting, setIsConverting] = useState(false);

    useEffect(() => {
        if (!file) {
            setImageSrc('');
            setPdfUrl('');
            setVideoUrl('');
            setAudioUrl('');
            return;
        }

        firebaseService.logEvent('file_preview_start', {
            fileType: file.type,
            fileSize: file.size,
        });

        // If a previewUrl is provided (downscaled thumbnail), use it as a
        // fast placeholder while the high-res version loads.
        if (
            previewUrl &&
            !isPdfFile(file) &&
            !isVideoFile(file) &&
            !isAudioFile(file)
        ) {
            setImageSrc(previewUrl);
        }

        let url: string | null = null;

        const setupPreview = async () => {
            if (isPdfFile(file)) {
                // For PDFs, create object URL directly
                url = URL.createObjectURL(file);
                setPdfUrl(url);
            } else if (isHeicFile(file)) {
                setIsConverting(true);
                try {
                    const convertedBlob = await convertToJpeg(file);
                    url = URL.createObjectURL(convertedBlob);
                    setImageSrc(url);
                } catch (err) {
                    error('HEIC conversion failed for preview:', err);
                    setImageSrc('');
                } finally {
                    setIsConverting(false);
                }
            } else if (isVideoFile(file)) {
                url = URL.createObjectURL(file);
                setVideoUrl(url);
            } else if (isAudioFile(file)) {
                url = URL.createObjectURL(file);
                setAudioUrl(url);
            } else {
                url = URL.createObjectURL(file);
                setImageSrc(url);
            }
        };

        setupPreview();

        return () => {
            if (url) {
                URL.revokeObjectURL(url);
            }
        };
    }, [file, previewUrl]);

    if (!file) return null;

    return (
        <div
            className="fixed inset-0 z-[2000] flex items-center justify-center bg-black bg-opacity-90 animate-fade-in-scale backdrop-blur-sm"
            onClick={onClose}
        >
            <div className="absolute top-4 right-4">
                <IconButton
                    onClick={onClose}
                    className="text-white bg-black/40 hover:bg-black/60 backdrop-blur-md shadow-lg border border-white/10"
                    size="large"
                >
                    <CloseIcon style={{ color: 'white' }} />
                </IconButton>
            </div>
            {isConverting ? (
                <CircularProgress style={{ color: 'white' }} size={60} />
            ) : pdfUrl ? (
                <iframe
                    src={pdfUrl}
                    className="w-[90vw] h-[90vh] border-0 shadow-2xl bg-white"
                    onClick={(e) => e.stopPropagation()} // Prevent close when clicking iframe
                    title={file.name}
                />
            ) : imageSrc ? (
                <img
                    src={imageSrc}
                    alt={file.name}
                    className="max-w-[90vw] max-h-[90vh] object-contain shadow-2xl transition-transform duration-300"
                    onClick={(e) => e.stopPropagation()} // Prevent close when clicking image
                />
            ) : videoUrl ? (
                <video
                    src={videoUrl}
                    controls
                    autoPlay
                    className="max-w-[90vw] max-h-[90vh] shadow-2xl"
                    onClick={(e) => e.stopPropagation()}
                />
            ) : audioUrl ? (
                <audio
                    src={audioUrl}
                    controls
                    autoPlay
                    className="w-[80vw] max-w-md shadow-2xl"
                    onClick={(e) => e.stopPropagation()}
                />
            ) : null}
        </div>
    );
}

function ReceivedFileItem({
    file,
    onPreview,
}: {
    file: File;
    onPreview: (file: File, previewUrl: string | null) => void;
}) {
    const [originalUrl, setOriginalUrl] = useState<string>('');
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [isConverting, setIsConverting] = useState(false);
    const [jpegBlob, setJpegBlob] = useState<Blob | null>(null);
    const { t } = useI18n();

    // Manage original file URL
    useEffect(() => {
        const url = URL.createObjectURL(file);
        setOriginalUrl(url);
        return () => {
            URL.revokeObjectURL(url);
        };
    }, [file]);

    // Manage preview URL and conversion
    useEffect(() => {
        let active = true;
        let conversionUrl: string | null = null;

        const generatePreview = async () => {
            if (isImageFile(file)) {
                setIsConverting(true);
                try {
                    const thumbBlob = await createThumbnail(file);
                    if (!active) return;
                    setJpegBlob(thumbBlob);
                    conversionUrl = URL.createObjectURL(thumbBlob);
                    setPreviewUrl(conversionUrl);
                } catch (err) {
                    error('Thumbnail generation failed:', err);
                    if (active) setPreviewUrl(null);
                } finally {
                    if (active) setIsConverting(false);
                }
            } else if (isVideoFile(file)) {
                setIsConverting(true);
                try {
                    const thumbUrl = await getVideoThumbnail(file);
                    if (!active) return;
                    setPreviewUrl(thumbUrl);
                } catch (err) {
                    error('Video thumbnail generation failed:', err);
                    if (active) setPreviewUrl(null);
                } finally {
                    if (active) setIsConverting(false);
                }
            } else if (isPdfFile(file)) {
                setPreviewUrl(null);
                setIsConverting(false);
            } else {
                setPreviewUrl(null);
                setIsConverting(false);
            }
        };

        generatePreview();

        return () => {
            active = false;
            if (conversionUrl) {
                URL.revokeObjectURL(conversionUrl);
            }
        };
    }, [file]); // Removed originalUrl from dependencies

    return (
        <div className="flex flex-col mb-8 w-full border-b pb-4 last:border-b-0">
            <div className="flex flex-row items-center w-full">
                <div className="w-[17.5%] mr-4 relative group shrink-0">
                    {isConverting ? (
                        <div className="flex items-center justify-center w-full aspect-square bg-gray-100 rounded-lg">
                            <CircularProgress size={32} />
                        </div>
                    ) : previewUrl ? (
                        <div
                            className="w-full aspect-square rounded-lg border border-gray-200 cursor-pointer overflow-hidden relative shadow-sm"
                            onClick={() => onPreview(file, previewUrl)}
                        >
                            <img
                                key={previewUrl}
                                src={previewUrl}
                                alt={file.name}
                                className="w-full h-full object-cover"
                            />
                            {(isVideoFile(file) || isAudioFile(file)) && (
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="bg-black bg-opacity-40 rounded-full p-2 flex items-center justify-center backdrop-blur-sm border border-white border-opacity-30 shadow-xl transform transition-transform group-hover:scale-110">
                                        <PlayArrowIcon
                                            className="text-white opacity-100"
                                            style={{ fontSize: '48px' }}
                                        />
                                    </div>
                                </div>
                            )}
                            {!isAudioFile(file) && (
                                <div className="absolute bottom-1 right-1 bg-black bg-opacity-40 rounded-full p-0.5 flex items-center justify-center backdrop-blur-sm border border-white border-opacity-20 shadow-md">
                                    <ZoomInIcon
                                        className="text-white drop-shadow-md"
                                        style={{ fontSize: '20px' }}
                                    />
                                </div>
                            )}
                        </div>
                    ) : isVideoFile(file) && !previewUrl ? (
                        <div
                            className="w-full aspect-square rounded-lg border border-gray-200 cursor-pointer overflow-hidden relative shadow-sm bg-white flex flex-col items-center justify-center hover:bg-gray-50 transition-colors"
                            onClick={() => onPreview(file, null)}
                        >
                            <VideocamIcon
                                style={{ fontSize: 40, color: '#1976d2' }}
                                className="mb-1"
                            />
                            <div className="absolute inset-0 flex items-center justify-center">
                                <PlayArrowIcon
                                    className="text-blue-500 opacity-10"
                                    style={{ fontSize: '48px' }}
                                />
                            </div>
                            <Typography
                                variant="caption"
                                className="text-gray-600 font-medium"
                            >
                                {t('file.video')}
                            </Typography>
                            <div className="absolute bottom-1 right-1 bg-black bg-opacity-10 rounded-full p-0.5 flex items-center justify-center border border-gray-200 shadow-sm transition-colors group-hover:bg-opacity-20">
                                <ZoomInIcon
                                    className="text-gray-600"
                                    style={{ fontSize: '20px' }}
                                />
                            </div>
                        </div>
                    ) : isAudioFile(file) ? (
                        <div
                            className="w-full aspect-square rounded-lg border border-gray-200 cursor-pointer overflow-hidden relative shadow-sm bg-white flex flex-col items-center justify-center hover:bg-gray-50 transition-colors"
                            onClick={() => onPreview(file, null)}
                        >
                            <div className="absolute inset-0 flex items-center justify-center">
                                <PlayArrowIcon
                                    className="text-blue-500 opacity-80"
                                    style={{ fontSize: '64px' }}
                                />
                            </div>
                        </div>
                    ) : isPdfFile(file) ? (
                        <div
                            className="w-full aspect-square rounded-lg border border-gray-200 cursor-pointer overflow-hidden relative shadow-sm bg-white flex flex-col items-center justify-center hover:bg-gray-50 transition-colors"
                            onClick={() => onPreview(file, null)}
                        >
                            <PictureAsPdfIcon
                                style={{ fontSize: 40, color: '#d32f2f' }}
                                className="mb-1"
                            />
                            <Typography
                                variant="caption"
                                className="text-gray-600 font-medium"
                            >
                                {t('file.pdf')}
                            </Typography>
                            <div className="absolute bottom-1 right-1 bg-black bg-opacity-10 rounded-full p-0.5 flex items-center justify-center border border-gray-200 shadow-sm transition-colors group-hover:bg-opacity-20">
                                <ZoomInIcon
                                    className="text-gray-600"
                                    style={{ fontSize: '20px' }}
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center w-full aspect-square bg-gray-50 rounded-lg border border-gray-200">
                            <InsertDriveFileIcon
                                style={{ fontSize: 30, color: '#bdbdbd' }}
                            />
                        </div>
                    )}
                </div>
                <div className="flex flex-col items-start justify-center flex-grow">
                    <Typography
                        variant="subtitle2"
                        className="font-bold mb-4 break-all text-gray-800 leading-tight text-left"
                    >
                        {file.name}{' '}
                        <span className="text-gray-600 font-normal text-xs ml-1">
                            ({filesize(file.size)})
                        </span>
                    </Typography>
                    <a
                        href={originalUrl}
                        download={file.name}
                        className="flex items-center text-blue-500 hover:text-blue-700 shrink-0 mb-3"
                    >
                        <DownloadIcon fontSize="small" className="mr-1" />
                        <Typography variant="body2" className="font-medium">
                            {t('file.download')}
                        </Typography>
                    </a>
                    {isHeicFile(file) ? (
                        <HeicConvertButton file={file} jpegBlob={jpegBlob} />
                    ) : null}
                </div>
            </div>
        </div>
    );
}

function HeicConvertButton({
    file,
    jpegBlob,
}: {
    file: File;
    jpegBlob: Blob | null;
}) {
    const [isConverting, setIsConverting] = useState(false);
    const { t } = useI18n();

    const handleDownloadAsJpeg = async () => {
        let blobToDownload = jpegBlob;

        if (!blobToDownload) {
            setIsConverting(true);
            try {
                blobToDownload = await convertToJpeg(file);
            } catch (err) {
                error('HEIC conversion failed:', err);
                alert(t('file.heicConversionFailed'));
                return;
            } finally {
                setIsConverting(false);
            }
        }

        if (blobToDownload) {
            const url = URL.createObjectURL(blobToDownload);
            const a = document.createElement('a');
            a.href = url;
            const name = file.name.replace(/\.(heic|heif)$/i, '') + '.jpeg';
            a.download = name;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            // Since this URL is created specifically for this download attempt,
            // we revoke it immediately after the click.
            setTimeout(() => URL.revokeObjectURL(url), 100);
        }
    };

    return (
        <Button
            variant="outlined"
            component="span"
            color="primary"
            size="small"
            onClick={handleDownloadAsJpeg}
            disabled={isConverting}
            startIcon={
                isConverting ? (
                    <CircularProgress size={16} color="inherit" />
                ) : null
            }
        >
            {isConverting ? t('file.converting') : t('file.downloadAsJpeg')}
        </Button>
    );
}

function renderReceivedFiles(
    files: { id: number; file: File }[],
    ref: React.RefObject<HTMLDivElement | null>,
    onPreview: (file: File, previewUrl: string | null) => void,
    feedbackFilesCount: number,
    sessionId: string,
    t: (key: string) => string,
) {
    if (files.length === 0) {
        return null;
    }

    return (
        <Card
            ref={ref}
            className="w-full max-w-2xl mx-auto m-3 relative"
            variant="outlined"
            sx={{
                borderRadius: '20px',
                backgroundColor: '#ffffff',
                borderColor: '#ffffff',
                borderWidth: '1px',
            }}
        >
            <CardContent>
                <Typography variant="h6">{t('start.receivedFiles')}</Typography>
                <Divider></Divider>
                <div style={{ height: 10 }}></div>
                <Typography variant="body2" className="mt-2 mb-2 text-gray-700">
                    {t('start.downloadManualHint')}
                </Typography>
                <div style={{ height: 10 }}></div>
                <div className="space-y-4">
                    {files.map((item) => (
                        <ReceivedFileItem
                            key={item.id}
                            file={item.file}
                            onPreview={onPreview}
                        />
                    ))}
                </div>
                <FeedbackSection
                    filesCount={feedbackFilesCount}
                    isSender={false}
                    sessionId={sessionId}
                />
            </CardContent>
        </Card>
    );
}

function renderReceivingFiles(
    files: Record<number, ReceivingFile>,
    t: (key: string) => string,
) {
    const fileList = Object.values(files);
    if (fileList.length === 0) return null;

    return (
        <div className="w-full flex flex-col items-center">
            {fileList.map((file) => (
                <Card
                    key={file.id}
                    className="w-full max-w-2xl mx-auto m-3 relative"
                    variant="outlined"
                    sx={{
                        borderRadius: '20px',
                        backgroundColor: '#ffffff',
                        borderColor: '#ffffff',
                        borderWidth: '1px',
                    }}
                >
                    <CardContent>
                        <Typography variant="h6" className="mb-4">
                            {t('start.receivingFile')}
                        </Typography>
                        <div className="mb-6 p-4 bg-blue-50 rounded-xl border border-blue-100 shadow-sm">
                            <Typography
                                variant="subtitle1"
                                className="font-bold break-all text-blue-900 leading-tight text-left"
                            >
                                {file.name}
                                <span className="text-blue-400 font-normal text-sm ml-2">
                                    ({filesize(file.size)})
                                </span>
                            </Typography>
                        </div>
                        <LinearProgress
                            variant={
                                file.uploadPending
                                    ? 'indeterminate'
                                    : 'determinate'
                            }
                            value={file.progress}
                            sx={{
                                height: 12,
                                borderRadius: 6,
                                backgroundColor: '#e0eaff',
                                '& .MuiLinearProgress-bar': { borderRadius: 6 },
                            }}
                        />
                        <div className="flex justify-between items-center mt-3">
                            <Typography
                                variant="caption"
                                className="text-blue-600 font-bold"
                            >
                                {file.uploadPending
                                    ? t('transfer.senderUploading')
                                    : `${file.progress}%`}
                            </Typography>
                            {file.progress > 0 && file.progress < 100 && (
                                <Typography
                                    variant="caption"
                                    className="text-gray-400 font-mono font-bold"
                                >
                                    {file.speed}
                                </Typography>
                            )}
                            <Typography
                                variant="caption"
                                className="text-gray-400 font-medium"
                            >
                                {filesize(file.size)}
                            </Typography>
                        </div>
                    </CardContent>
                </Card>
            ))}
        </div>
    );
}

function renderHowItWorksList(
    t: (key: string) => string,
    locale: string,
    options?: StartCtaLinkOptions,
) {
    const guideTitleSeparator = locale === 'ja' || locale === 'th' ? '' : ' ';
    const steps = [
        {
            stepLabel: t('start.how.step1Label'),
            cardTitle: t('start.how.step1CardTitle'),
            cardDescription: t('start.how.step1CardBody'),
            detailsTitle: t('start.how.step1DetailsTitle'),
            description: t('start.how.step1DetailsBody'),
            imageSrc: '/how-to-step1.png?v=2',
            imageAlt: 'Phone scanning a QR code from a laptop screen',
            backgroundColor: 'rgb(249, 250, 252)',
            borderColor: '#dbe6f3',
        },
        {
            stepLabel: t('start.how.step2Label'),
            cardTitle: t('start.how.step2CardTitle'),
            cardDescription: t('start.how.step2CardBody'),
            detailsTitle: t('start.how.step2DetailsTitle'),
            description: t('start.how.step2DetailsBody'),
            imageSrc: '/how-to-step2.png?v=2',
            imageAlt: 'Phone selecting files to send',
            backgroundColor: '#f8fafc',
            borderColor: '#d9e4ef',
        },
        {
            stepLabel: t('start.how.step3Label'),
            cardTitle: t('start.how.step3CardTitle'),
            cardDescription: t('start.how.step3CardBody'),
            detailsTitle: t('start.how.step3DetailsTitle'),
            description: t('start.how.step3DetailsBody'),
            imageSrc: '/how-to-step3.png?v=2',
            imageAlt: 'Phone sending files to a computer',
            backgroundColor: '#f9fafb',
            borderColor: '#dbe3ea',
        },
    ];

    return (
        <section
            id="fileplease-how-it-works"
            className="fileplease-flow-section"
        >
            <div className="fileplease-flow-inner">
                <div className="fileplease-flow-heading">
                    <Typography
                        component="p"
                        variant="overline"
                        sx={{
                            color: '#2563EB',
                            fontWeight: 800,
                            letterSpacing: 0,
                        }}
                    >
                        {t('start.howItWorks')}
                    </Typography>
                    <Typography
                        component="h2"
                        variant="h4"
                        sx={{
                            color: '#0f172a',
                            fontWeight: 800,
                            lineHeight: 1.08,
                            mt: 1,
                        }}
                    >
                        {t('start.how.guideTitlePrefix')}
                        {guideTitleSeparator}
                        <span className="text-blue-600">
                            {t('start.how.guideTitleAccent')}
                        </span>
                    </Typography>
                </div>

                <div className="fileplease-step-list">
                    {steps.map((step) => (
                        <article
                            key={step.stepLabel}
                            className="fileplease-step-row"
                        >
                            <div className="fileplease-step-media">
                                <img
                                    src={step.imageSrc}
                                    alt={step.imageAlt}
                                    loading="lazy"
                                />
                            </div>
                            <div className="fileplease-step-copy">
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: '#2563EB',
                                        fontWeight: 800,
                                        letterSpacing: 0,
                                    }}
                                >
                                    {step.stepLabel}
                                </Typography>
                                <Typography
                                    component="h3"
                                    variant="h6"
                                    sx={{
                                        color: '#0f172a',
                                        fontWeight: 800,
                                        lineHeight: 1.24,
                                        mt: 0.75,
                                    }}
                                >
                                    {step.detailsTitle}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: '#334155',
                                        fontWeight: 650,
                                        lineHeight: 1.65,
                                        mt: 1,
                                    }}
                                >
                                    {step.cardDescription}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: '#475569',
                                        lineHeight: 1.75,
                                        mt: 0.85,
                                    }}
                                >
                                    {step.description}
                                </Typography>
                            </div>
                        </article>
                    ))}
                </div>

                <div className="fileplease-proof-band">
                    <div className="fileplease-proof-copy">
                        <Typography
                            component="h3"
                            variant="h6"
                            sx={{
                                color: '#0f172a',
                                fontWeight: 800,
                                lineHeight: 1.25,
                            }}
                        >
                            {t('start.privacyByDesign')}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: '#475569',
                                lineHeight: 1.7,
                                mt: 1,
                            }}
                        >
                            {t('start.how.private')} {t('start.how.noStorage')}
                        </Typography>
                    </div>
                    <div className="fileplease-proof-list">
                        <span>
                            <HttpsIcon sx={{ fontSize: '1rem' }} />
                            {t('start.how.private')}
                        </span>
                        <span>
                            <PersonOffIcon sx={{ fontSize: '1rem' }} />
                            {t('start.how.noAccount')}
                        </span>
                        <span>
                            <CloudOffIcon sx={{ fontSize: '1rem' }} />
                            {t('start.how.noStorage')}
                        </span>
                    </div>
                    {options && (
                        <Button
                            variant="contained"
                            startIcon={
                                <SendIcon
                                    sx={{
                                        transform:
                                            'translateY(-3px) rotate(-45deg)',
                                    }}
                                />
                            }
                            onClick={options.onCtaClick}
                            disabled={options.ctaDisabled}
                            sx={{
                                borderRadius: '9999px',
                                textTransform: 'none',
                                px: 3,
                                py: 1.35,
                                fontWeight: 800,
                                backgroundColor: '#2563EB',
                                boxShadow: 'none',
                                whiteSpace: 'nowrap',
                                '&:hover': {
                                    backgroundColor: '#1d4ed8',
                                    boxShadow:
                                        '0 14px 34px rgba(37, 99, 235, 0.28)',
                                },
                            }}
                        >
                            {options.ctaLabel}
                        </Button>
                    )}
                </div>
            </div>
        </section>
    );
}

type FaqItem = {
    question: string;
    answer: string;
};

type HighIntentSeoCardContent = {
    title: string;
    intro: string;
    highlightsTitle: string;
    highlights: string[];
    detailsTitle: string;
    details: string;
};

const HIGH_INTENT_TRANSLATION_PREFIXES: Record<string, string> = {
    'share-photos-from-iphone-to-pc': 'sharePhotosIphonePc',
    'airdrop-for-windows': 'airdropWindows',
    'transfer-iphone-to-pc-without-itunes': 'transferIphoneToPcWithoutItunes',
    'send-photos-from-iphone-to-computer': 'sendPhotosIphoneToComputer',
    'send-photos-from-android-to-computer': 'sendPhotosAndroidToComputer',
    'send-photos-from-android-to-pc': 'sendPhotosAndroidToPc',
    'send-large-photos-from-iphone': 'sendLargePhotosIphone',
    'send-apk-files-from-computer-to-android': 'sendApkComputerToAndroid',
    'get-large-files-from-iphone': 'getLargeFilesFromIphone',
    'get-large-videos-from-iphone': 'getLargeVideosFromIphone',
};

const SEO_SECTION_KEYS = [
    'meaning',
    'webrtc',
    'flow',
    'privacy',
    'noStorage',
    'whenToUse',
] as const;

const SEO_LIMITATION_KEYS = [1, 2, 3, 4] as const;
const SEO_FAQ_KEYS = [1, 2, 3, 4, 5, 6, 7, 8] as const;

function getLongFormSeoSections(t: (key: string) => string) {
    return SEO_SECTION_KEYS.map((sectionKey) => ({
        title: t(`seo.longform.section.${sectionKey}.title`),
        paragraphs: [
            t(`seo.longform.section.${sectionKey}.p1`),
            t(`seo.longform.section.${sectionKey}.p2`),
        ],
    }));
}

function getLongFormLimitations(t: (key: string) => string) {
    return SEO_LIMITATION_KEYS.map((key) =>
        t(`seo.longform.limitations.item${key}`),
    );
}

function getFaqItems(
    t: (key: string) => string,
    pathname: string,
    locale: string,
): FaqItem[] {
    const items = SEO_FAQ_KEYS.map((key) => ({
        question: t(`seo.faq.q${key}`),
        answer: t(`seo.faq.a${key}`),
    }));

    const localizedPrefix = `/${locale}/`;
    const slug = pathname.startsWith(localizedPrefix)
        ? pathname.slice(localizedPrefix.length)
        : '';
    const highIntentFaqItem = getHighIntentFaqItem(t, slug);

    if (highIntentFaqItem) {
        items.push(highIntentFaqItem);
    }

    if (!highIntentFaqItem && pathname === `/${locale}/airdrop-for-windows`) {
        items.push({
            question: t('seo.faq.airdropQuestion'),
            answer: t('seo.faq.airdropAnswer'),
        });
    }

    return items;
}

function getHighIntentSeoCardContent(
    t: (key: string) => string,
    pathname: string,
    locale: string,
): HighIntentSeoCardContent | null {
    const localizedPrefix = `/${locale}/`;
    if (!pathname.startsWith(localizedPrefix)) {
        return null;
    }

    const slug = pathname.slice(localizedPrefix.length);
    const prefix = HIGH_INTENT_TRANSLATION_PREFIXES[slug];
    if (!prefix) {
        return null;
    }

    const titleKey = `highIntentSeo.${prefix}.title`;
    const title = t(titleKey);
    if (title === titleKey) {
        return null;
    }

    return {
        title,
        intro: t(`highIntentSeo.${prefix}.intro`),
        highlightsTitle: t(`highIntentSeo.${prefix}.highlightsTitle`),
        highlights: [
            t(`highIntentSeo.${prefix}.highlight1`),
            t(`highIntentSeo.${prefix}.highlight2`),
            t(`highIntentSeo.${prefix}.highlight3`),
        ],
        detailsTitle: t(`highIntentSeo.${prefix}.detailsTitle`),
        details: t(`highIntentSeo.${prefix}.details`),
    };
}

function getHighIntentFaqItem(
    t: (key: string) => string,
    slug: string,
): FaqItem | null {
    const prefix = HIGH_INTENT_TRANSLATION_PREFIXES[slug];
    if (!prefix) {
        return null;
    }

    const questionKey = `highIntentFaq.${prefix}.question`;
    const answerKey = `highIntentFaq.${prefix}.answer`;
    const question = t(questionKey);
    const answer = t(answerKey);

    if (question === questionKey || answer === answerKey) {
        return null;
    }

    return {
        question,
        answer,
    };
}

type SeoContentSectionProps = {
    locale: string;
    pathname: string;
    t: (key: string) => string;
    showLongFormCard: boolean;
    faqCardRef: React.RefObject<HTMLDivElement | null>;
    longFormCtaOptions?: StartCtaLinkOptions;
};

const SeoContentSection = React.memo(function SeoContentSection({
    locale,
    pathname,
    t,
    showLongFormCard,
    faqCardRef,
    longFormCtaOptions,
}: SeoContentSectionProps) {
    const longFormSections = getLongFormSeoSections(t);
    const limitations = getLongFormLimitations(t);
    const faqItems = getFaqItems(t, pathname, locale);
    const highIntentSeoCard = getHighIntentSeoCardContent(t, pathname, locale);

    const faqSchema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqItems.map((item) => ({
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: {
                '@type': 'Answer',
                text: item.answer,
            },
        })),
    };

    const popularTransferLinks = [
        {
            path: 'share-photos-from-iphone-to-pc',
            label: t('highIntent.sharePhotosIphonePc.title'),
        },
        {
            path: 'airdrop-for-windows',
            label: t('highIntent.airdropWindows.title'),
        },
        {
            path: 'transfer-iphone-to-pc-without-itunes',
            label: t('highIntent.transferIphoneToPcWithoutItunes.title'),
        },
        {
            path: 'send-photos-from-iphone-to-computer',
            label: t('highIntent.sendPhotosIphoneToComputer.title'),
        },
        {
            path: 'send-photos-from-android-to-computer',
            label: t('highIntent.sendPhotosAndroidToComputer.title'),
        },
        {
            path: 'send-large-photos-from-iphone',
            label: t('highIntent.sendLargePhotosIphone.title'),
        },
        {
            path: 'send-apk-files-from-computer-to-android',
            label: t('highIntent.sendApkComputerToAndroid.title'),
        },
        {
            path: 'get-large-files-from-iphone',
            label: t('highIntent.getLargeFilesFromIphone.title'),
        },
        {
            path: 'get-large-videos-from-iphone',
            label: t('highIntent.getLargeVideosFromIphone.title'),
        },
    ];

    return (
        <>
            <script type="application/ld+json">
                {JSON.stringify(faqSchema)}
            </script>
            {showLongFormCard && highIntentSeoCard && (
                <Card
                    className="w-full max-w-2xl mx-auto m-3 relative"
                    variant="outlined"
                    sx={{
                        borderRadius: '20px',
                        backgroundColor: '#ffffff',
                        borderColor: '#ffffff',
                        borderWidth: '1px',
                    }}
                >
                    <CardContent sx={{ p: { xs: 2.75, sm: 3.25 } }}>
                        <Typography
                            component="h2"
                            variant="h5"
                            className="font-semibold text-gray-900 text-left"
                            sx={{ mb: { xs: 3, sm: 3.5 } }}
                        >
                            {highIntentSeoCard.title}
                        </Typography>
                        <Typography
                            variant="body2"
                            className="text-gray-700 text-left leading-7 mb-7"
                        >
                            {highIntentSeoCard.intro}
                        </Typography>
                        <Typography
                            component="h3"
                            variant="h6"
                            className="font-semibold text-gray-900 text-left mb-4"
                        >
                            {highIntentSeoCard.highlightsTitle}
                        </Typography>
                        <List sx={{ py: 0, mb: 1 }}>
                            {highIntentSeoCard.highlights.map((item) => (
                                <ListItem
                                    key={item}
                                    disablePadding
                                    sx={{ py: 0.2 }}
                                >
                                    <ListItemText
                                        primary={`• ${item}`}
                                        primaryTypographyProps={{
                                            variant: 'body2',
                                            className:
                                                'text-gray-700 text-left leading-6',
                                        }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                        <Typography
                            component="h3"
                            variant="h6"
                            className="font-semibold text-gray-900 text-left mb-4 mt-9"
                        >
                            {highIntentSeoCard.detailsTitle}
                        </Typography>
                        <Typography
                            variant="body2"
                            className="text-gray-700 text-left leading-7"
                        >
                            {highIntentSeoCard.details}
                        </Typography>
                    </CardContent>
                </Card>
            )}
            {showLongFormCard && (
                <Card
                    className="w-full max-w-2xl mx-auto m-3 relative"
                    variant="outlined"
                    sx={{
                        borderRadius: '20px',
                        backgroundColor: '#ffffff',
                        borderColor: '#ffffff',
                        borderWidth: '1px',
                    }}
                >
                    <CardContent sx={{ p: { xs: 2.75, sm: 3.25 } }}>
                        <Typography
                            component="h2"
                            variant="h5"
                            className="font-semibold text-gray-900 text-left mb-10 pb-2"
                        >
                            {t('seo.longform.heading')}
                        </Typography>
                        {longFormSections.map((section) => (
                            <div key={section.title} className="mb-8 last:mb-0">
                                <Typography
                                    component="h3"
                                    variant="h6"
                                    className="font-semibold text-gray-900 text-left mb-3"
                                >
                                    {section.title}
                                </Typography>
                                {section.paragraphs.map((paragraph) => (
                                    <Typography
                                        key={paragraph}
                                        variant="body2"
                                        className="text-gray-700 text-left leading-7 mb-4"
                                    >
                                        {paragraph}
                                    </Typography>
                                ))}
                            </div>
                        ))}

                        <Typography
                            component="h3"
                            variant="h6"
                            className="font-semibold text-gray-900 text-left mb-3 mt-8"
                        >
                            {t('seo.longform.limitations.title')}
                        </Typography>
                        <List sx={{ py: 0, mb: 0 }}>
                            {limitations.map((item) => (
                                <ListItem
                                    key={item}
                                    disablePadding
                                    sx={{ py: 0.2 }}
                                >
                                    <ListItemText
                                        primary={`• ${item}`}
                                        primaryTypographyProps={{
                                            variant: 'body2',
                                            className:
                                                'text-gray-700 text-left leading-6',
                                        }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                        {renderStartCtaLink(longFormCtaOptions, 3)}
                    </CardContent>
                </Card>
            )}

            <Card
                id="faq-card"
                ref={faqCardRef}
                className="w-full max-w-2xl mx-auto m-3 relative"
                variant="outlined"
                sx={{
                    borderRadius: '20px',
                    backgroundColor: '#ffffff',
                    borderColor: '#ffffff',
                    borderWidth: '1px',
                }}
            >
                <CardContent>
                    <Typography
                        component="h2"
                        variant="h5"
                        className="font-semibold text-gray-900 text-left mb-3"
                    >
                        {t('seo.faq.heading')}
                    </Typography>
                    <List sx={{ py: 0 }}>
                        {faqItems.map((item) => (
                            <ListItem
                                key={item.question}
                                disablePadding
                                sx={{
                                    display: 'block',
                                    py: 1.2,
                                    borderBottom: '1px solid #f0f0f0',
                                }}
                            >
                                <Typography
                                    component="h3"
                                    variant="subtitle1"
                                    className="font-semibold text-gray-900 text-left mb-1"
                                >
                                    {item.question}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    className="text-gray-700 text-left leading-7"
                                >
                                    {item.answer}
                                </Typography>
                            </ListItem>
                        ))}
                    </List>
                    <br />
                    <Typography
                        component="h2"
                        variant="h6"
                        className="font-semibold text-gray-900 text-left mt-8 mb-2"
                    >
                        {t('seo.popularTransfers.heading')}
                    </Typography>
                    <List sx={{ py: 0 }}>
                        {popularTransferLinks.map((link) => (
                            <ListItem
                                key={link.path}
                                disablePadding
                                sx={{ py: 0.5 }}
                            >
                                <a
                                    href={`/${locale}/${link.path}`}
                                    className="text-blue-700 hover:text-blue-900 underline text-left text-sm font-medium"
                                >
                                    {link.label}
                                </a>
                            </ListItem>
                        ))}
                    </List>
                </CardContent>
            </Card>
        </>
    );
});

function UrlSection({ url }: { url: string }) {
    const [openButton, setOpenButton] = useState(false);
    const { t } = useI18n();

    const handleCopyButton = () => {
        navigator.clipboard.writeText(url);
        setOpenButton(true);
        setTimeout(() => setOpenButton(false), 2000);
    };

    const LinkContent = (
        <span className="flex items-center gap-0.5">
            <LockIcon sx={{ fontSize: '0.9rem', color: 'inherit' }} />{' '}
            {t('start.link')}
        </span>
    );

    return (
        <div className="flex flex-row items-center justify-center mt-4 gap-2 animate-fade-in anim-delay-500">
            <Typography variant="body1" className="font-medium text-gray-700">
                {t('start.orShareThis')}
            </Typography>
            <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className="text-blue-700 hover:text-blue-900 underline text-sm font-medium flex items-center gap-0.5"
            >
                {LinkContent}
            </a>
            <Tooltip
                open={openButton}
                title={t('start.copiedToClipboard')}
                placement="bottom"
                slotProps={{
                    tooltip: {
                        sx: {
                            backgroundColor: '#e3f2fd',
                            color: '#1976d2',
                            fontWeight: 600,
                            border: '1px solid #bbdefb',
                        },
                    },
                    arrow: {
                        sx: {
                            color: '#e3f2fd',
                        },
                    },
                }}
            >
                <Button
                    onClick={handleCopyButton}
                    size="small"
                    variant="outlined"
                    startIcon={<ContentCopyIcon sx={{ fontSize: '0.9rem' }} />}
                    sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        ml: 1,
                        borderRadius: '8px',
                        px: 1.5,
                        lineHeight: 1.2,
                        height: '28px',
                    }}
                >
                    {t('feedback.copyLink')}
                </Button>
            </Tooltip>
        </div>
    );
}

interface StartProps {
    titleLabel?: string;
    subtitleLabel?: string;
    ctaLabel?: string;
    descriptionLabel?: string;
    descriptionSuffixLabel?: string;
}

function Start({
    titleLabel = 'Send photos & files instantly between devices',
    subtitleLabel = 'No app. No cloud storage. Peer-to-peer.',
    ctaLabel = 'Start Transfer',
    descriptionLabel,
    descriptionSuffixLabel,
}: StartProps) {
    const { t, locale } = useI18n();
    const navigate = useNavigate();
    const location = useLocation();
    const shouldRenderSeoContent = true;
    const seoDescription =
        descriptionLabel ??
        [subtitleLabel, descriptionSuffixLabel].filter(Boolean).join(' ');
    const faqCardRef = useRef<HTMLDivElement | null>(null);
    const transferPanelRef = useRef<HTMLElement | null>(null);
    const [sessionId, setSessionId] = useState(() =>
        StartConfig.HIGH_INTENT ? '' : CommonUtils.randomBase62(16),
    );
    const [encryptionKey, setEncryptionKey] = useState<CryptoKey | null>(null);
    const [keyStr, setKeyStr] = useState<string>('');
    const [showQR, setShowQR] = useState(!StartConfig.HIGH_INTENT);
    const [shouldRenderQrCode, setShouldRenderQrCode] = useState(false);
    const [isFakeConnecting, setIsFakeConnecting] = useState(false);

    const revealTransferPanelOnMobile = () => {
        if (
            !window.matchMedia('(max-width: 960px)').matches ||
            !transferPanelRef.current
        ) {
            return;
        }

        window.setTimeout(() => {
            if (transferPanelRef.current) {
                scrollToElementRespectingHeader(
                    transferPanelRef.current,
                    'smooth',
                );
            }
        }, 80);
    };

    const handleStartClick = async (
        location: 'initial_view' | 'continuation_view',
    ) => {
        if (isFakeConnecting) return;

        firebaseService.logEvent('start_sharing_click', {
            page_path: window.location.pathname,
            button_location: location,
            ...UtmUtils.getCapturedParams(),
        });

        if (StartConfig.FAKE_CONNECT_DELAY > 0) {
            setIsFakeConnecting(true);
            await new Promise((resolve) =>
                setTimeout(resolve, StartConfig.FAKE_CONNECT_DELAY),
            );
            setIsFakeConnecting(false);
        }

        if (
            location === 'continuation_view' &&
            wasConnectedRef.current &&
            !isConnected
        ) {
            handleStartNewSession();
        } else {
            if (location === 'initial_view') {
                let sid = sessionId;
                if (!sid) {
                    sid = CommonUtils.randomBase62(16);
                    setSessionId(sid);
                }
                window.setTimeout(() => {
                    void firebaseService.logSessionStart(sid);
                }, 0);
            }
            setShouldRenderQrCode(false);
            setShowQR(true);
            revealTransferPanelOnMobile();
        }
    };

    const handleStartNewSession = async () => {
        const newSessionId = CommonUtils.randomBase62(16);

        // Go online if we were timed out
        await firebaseService.goOnline();

        // Regenerate key
        const key = await CommonUtils.generateKey();
        const exported = await CommonUtils.exportKey(key);

        // Update all state at once to minimize re-renders
        setSessionId(newSessionId);
        setEncryptionKey(key);
        setKeyStr(exported);
        setReceivedFiles([]);
        setReceivingFiles({});
        setIsConnected(false);
        setIsConnecting(false);
        setHasConnected(false);
        setShowClosedModal(false);
        setClosedModalDismissed(false);
        setShowTimeoutModal(false); // Clear timeout modal if open
        wasConnectedRef.current = false;

        // Crucial: Show QR immediately because this is a "high intent" continuation
        setShouldRenderQrCode(false);
        setShowQR(true);
        revealTransferPanelOnMobile();
        window.setTimeout(() => {
            void firebaseService.logSessionStart(newSessionId);
        }, 0);
    };

    useEffect(() => {
        const initKey = async () => {
            const key = await CommonUtils.generateKey();
            setEncryptionKey(key);
            const exported = await CommonUtils.exportKey(key);
            setKeyStr(exported);
        };
        initKey();
    }, []);

    const uploadUrl = useMemo(() => {
        if (!keyStr || !sessionId) return '';
        const baseUrl = `${window.location.origin}/${locale}/send?sessionId=${sessionId}`;
        const relayQuery = isRelayOnlyRequested(location.search)
            ? '&relay_only=1'
            : '';
        const serverQuery =
            new URLSearchParams(location.search).get('server_only') === '1'
                ? '&server_only=1'
                : '';
        return `${baseUrl}${relayQuery}${serverQuery}#key=${encodeURIComponent(keyStr)}`;
    }, [sessionId, keyStr, locale, location.search]);
    const [receivingFiles, setReceivingFiles] = useState<
        Record<number, ReceivingFile>
    >({});
    const [receivedFiles, setReceivedFiles] = useState<
        { id: number; file: File }[]
    >([]);
    const [isConnected, setIsConnected] = useState(false);
    const isConnectedRef = useRef(false);
    const [isConnecting, setIsConnecting] = useState(false);
    const [hasConnected, setHasConnected] = useState(false);
    const [showClosedModal, setShowClosedModal] = useState(false);
    const [showTimeoutModal, setShowTimeoutModal] = useState(false);
    const [showP2PBlockedModal, setShowP2PBlockedModal] = useState(false);
    const [p2pBlockedReason] = useState<'webrtc_unsupported' | null>(null);
    const [closedModalDismissed, setClosedModalDismissed] = useState(false);
    const wasConnectedRef = useRef(false);
    const [previewFile, setPreviewFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const receivedFilesRef = useRef<HTMLDivElement>(null);
    const [legalType, setLegalType] = useState<'privacy' | 'tos' | null>(null);

    const [sendFiles, setSendFiles] = useState<SendFileProps[]>([]);
    const [sendProgress, setSendProgress] = useState<Record<string, number>>(
        {},
    );
    const [sendErrors, setSendErrors] = useState<Record<string, string | null>>(
        {},
    );
    const [sendSpeeds, setSendSpeeds] = useState<Record<string, string>>({});
    const [currentlySendingId, setCurrentlySendingId] = useState<string | null>(
        null,
    );
    const sendTransferMetaRef = useRef<Record<string, { startTime: number }>>(
        {},
    );
    const sendTransferIdToEntryIdRef = useRef<Record<number, string>>({});
    const receiverRef = useRef<FileReceiver | null>(null);
    const [activeSignaling, setActiveSignaling] = useState<Signaling | null>(
        null,
    );
    const hasActiveReceivingTransfers = useMemo(
        () => Object.keys(receivingFiles).length > 0,
        [receivingFiles],
    );
    const hasActiveSendingTransfers = useMemo(
        () =>
            currentlySendingId !== null ||
            sendFiles.some((fileItem) => {
                const progress = sendProgress[fileItem.id] || 0;
                const hasError = !!sendErrors[fileItem.id];
                return (
                    fileItem.file !== null &&
                    !hasError &&
                    progress > 0 &&
                    progress < 100
                );
            }),
        [currentlySendingId, sendErrors, sendFiles, sendProgress],
    );
    const hasActiveTransfers =
        hasActiveReceivingTransfers || hasActiveSendingTransfers;
    const completedSentFilesCount = useMemo(
        () =>
            Object.values(sendProgress).filter((progress) => progress === 100)
                .length,
        [sendProgress],
    );
    const completedReceivedFilesCount = receivedFiles.length;
    const feedbackFilesCount =
        completedSentFilesCount + completedReceivedFilesCount;

    const handleHowToStartClick = (
        location: 'initial_view' | 'continuation_view',
    ) => {
        scrollPageToTop();
        window.requestAnimationFrame(() => {
            void handleStartClick(location);
        });
    };

    const currentHowToCtaLabel = isFakeConnecting
        ? t('start.creatingConnection')
        : ctaLabel;
    const howToStartOptions: StartCtaLinkOptions | undefined =
        wasConnectedRef.current && !isConnected
            ? {
                ctaLabel: currentHowToCtaLabel,
                onCtaClick: () => {
                    handleHowToStartClick('continuation_view');
                },
                ctaDisabled: isFakeConnecting,
            }
            : !showQR
                ? {
                    ctaLabel: currentHowToCtaLabel,
                    onCtaClick: () => {
                        handleHowToStartClick('initial_view');
                    },
                    ctaDisabled: isFakeConnecting,
                }
                : undefined;

    const handleFaqMenuClick = () => {
        if (shouldRenderSeoContent && faqCardRef.current) {
            scrollToElementRespectingHeader(faqCardRef.current);
            return;
        }

        navigate(`/${locale}#faq`);
    };

    const handleSupportClick = () => {
        void firebaseService.logEvent('contact_support_click', {
            button_location: 'footer',
            page_path: window.location.pathname,
        });
    };

    useEffect(() => {
        if (!shouldRenderSeoContent) {
            return;
        }

        if (window.location.hash !== '#faq') {
            return;
        }

        const raf = window.requestAnimationFrame(() => {
            if (faqCardRef.current) {
                scrollToElementRespectingHeader(faqCardRef.current);
            }
        });

        return () => window.cancelAnimationFrame(raf);
    }, [shouldRenderSeoContent, isConnected]);

    useEffect(() => {
        if (!showP2PBlockedModal || !p2pBlockedReason) {
            return;
        }

        void firebaseService.logEvent('p2p_blocked_dialog_shown', {
            page_path: window.location.pathname,
            screen: 'start',
            reason: p2pBlockedReason,
        });
    }, [showP2PBlockedModal, p2pBlockedReason]);

    useEffect(() => {
        if (!hasActiveTransfers) {
            return;
        }

        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = '';
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [hasActiveTransfers]);

    useEffect(() => {
        if (!encryptionKey || !showQR) return;

        const signaling = new Signaling(sessionId, 'caller');
        setActiveSignaling(signaling);
        let reconnectTimer: number | null = null;
        let disposed = false;
        let lastReconnectRequestId: string | null = null;
        const ansSub = signaling.answer$.subscribe((answer) => {
            if (answer && !isConnectedRef.current) {
                setIsConnecting(true);
            }
        });

        const attachSendProgress = (receiver: FileReceiver) => {
            receiver.setSendProgressCallback((id, progress, bytesSent) => {
                const entryId = sendTransferIdToEntryIdRef.current[id];
                if (!entryId) return;
                const meta = sendTransferMetaRef.current[entryId];
                if (meta) {
                    const elapsed = Date.now() - meta.startTime;
                    const speed = CommonUtils.formatSpeed(bytesSent, elapsed);
                    setSendSpeeds((prev) => ({ ...prev, [entryId]: speed }));
                }
                setSendProgress((prev) => ({ ...prev, [entryId]: progress }));
            });
        };

        const replaceReceiver = () => {
            if (disposed || signaling.serverEnabled) return;
            reconnectTimer = null;
            setReceivingFiles({});
            setIsConnecting(true);
            setShowClosedModal(false);
            receiverRef.current?.destroy();
            const nextReceiver = createFileReceiver(
                signaling,
                encryptionKey,
                (id, file) =>
                    setReceivedFiles((prev) => [{ id, file }, ...prev]),
                setIsConnected,
                isConnectedRef,
                setIsConnecting,
                setReceivingFiles,
                sessionId,
                handleReceiverStateChange,
            );
            receiverRef.current = nextReceiver;
            attachSendProgress(nextReceiver);
            void firebaseService.logEvent('connection_reconnect_started', {
                session_id: sessionId,
                reason: 'connection_interrupted',
            });
        };

        const scheduleReconnect = (delayMs: number) => {
            if (disposed || signaling.serverEnabled || reconnectTimer !== null)
                return;
            reconnectTimer = window.setTimeout(replaceReceiver, delayMs);
        };

        function handleReceiverStateChange(
            state: RTCPeerConnectionState,
            receiver: FileReceiver,
        ) {
            if (disposed || receiverRef.current !== receiver) return;
            if (state === 'connected') {
                if (reconnectTimer !== null) {
                    window.clearTimeout(reconnectTimer);
                    reconnectTimer = null;
                }
                setShowClosedModal(false);
                return;
            }
            if (signaling.serverEnabled) return;
            if (state === 'failed' || state === 'closed') {
                scheduleReconnect(0);
            } else if (state === 'disconnected') {
                scheduleReconnect(5_000);
            }
        }

        const receiver = createFileReceiver(
            signaling,
            encryptionKey,
            (id, file) => setReceivedFiles((prev) => [{ id, file }, ...prev]),
            setIsConnected,
            isConnectedRef,
            setIsConnecting,
            setReceivingFiles,
            sessionId,
            handleReceiverStateChange,
        );

        receiverRef.current = receiver;
        attachSendProgress(receiver);

        const reconnectSub = signaling.reconnectRequest$.subscribe(
            (request) => {
                if (
                    signaling.serverEnabled ||
                    !request ||
                    request.id === lastReconnectRequestId ||
                    request.attemptId !== signaling.getActiveAttemptId()
                ) {
                    return;
                }
                lastReconnectRequestId = request.id;
                scheduleReconnect(0);
            },
        );

        return () => {
            disposed = true;
            if (reconnectTimer !== null) {
                window.clearTimeout(reconnectTimer);
            }
            const activeReceiver = receiverRef.current;
            receiverRef.current = null;
            ansSub.unsubscribe();
            reconnectSub.unsubscribe();
            activeReceiver?.destroy();
            signaling.destroy();
        };
    }, [sessionId, encryptionKey, showQR]);

    useEffect(() => {
        if (!showQR || !uploadUrl) {
            setShouldRenderQrCode(false);
            return;
        }

        let timeoutId: number | null = null;
        const rafId = window.requestAnimationFrame(() => {
            timeoutId = window.setTimeout(() => {
                void loadQRCodeStylingModule();
                setShouldRenderQrCode(true);
            }, 0);
        });

        return () => {
            window.cancelAnimationFrame(rafId);
            if (timeoutId !== null) {
                window.clearTimeout(timeoutId);
            }
        };
    }, [showQR, uploadUrl]);

    useEffect(() => {
        if (!showQR || isConnected || hasConnected || showTimeoutModal) return;

        const timer = setTimeout(async () => {
            log('Session timed out. Going offline...');
            // Go offline
            await firebaseService.goOffline();

            // Show modal
            setShowTimeoutModal(true);
        }, StartConfig.SESSION_TIMEOUT_MS);

        return () => clearTimeout(timer);
    }, [showQR, isConnected, hasConnected, sessionId, showTimeoutModal]);

    useEffect(() => {
        // Only scroll on the first file received
        if (receivedFiles.length === 1 && receivedFilesRef.current) {
            scrollToElementRespectingHeader(receivedFilesRef.current);
        }
    }, [receivedFiles.length]);

    useEffect(() => {
        if (isConnected) {
            setHasConnected(true);
            wasConnectedRef.current = true;
            return;
        }

        if (
            !wasConnectedRef.current ||
            showClosedModal ||
            closedModalDismissed ||
            receivedFiles.length > 0
        ) {
            return;
        }

        const timer = window.setTimeout(() => setShowClosedModal(true), 30_000);
        return () => window.clearTimeout(timer);
    }, [
        isConnected,
        showClosedModal,
        closedModalDismissed,
        receivedFiles.length,
    ]);

    const sendFileWithProgress = async (file: File, entryId: string) => {
        const receiver = receiverRef.current;
        if (!receiver) return;

        const transferId = Math.floor(Math.random() * 0xffffffff);
        sendTransferIdToEntryIdRef.current[transferId] = entryId;
        sendTransferMetaRef.current[entryId] = { startTime: Date.now() };
        setSendProgress((prev) => ({ ...prev, [entryId]: 0 }));
        setSendErrors((prev) => ({ ...prev, [entryId]: null }));
        setSendSpeeds((prev) => ({ ...prev, [entryId]: '' }));
        try {
            await receiver.sendFile(file, transferId);
            setSendProgress((prev) => ({ ...prev, [entryId]: 100 }));
            const connectionType = await receiver.getConnectionType();
            const meta = sendTransferMetaRef.current[entryId];
            const transferDuration = meta ? Date.now() - meta.startTime : 0;
            firebaseService.logEvent('file_sent_success', {
                fileExtension: file.name.split('.').pop(),
                fileSize: file.size,
                fileType: file.type,
                transferId,
                connectionType,
                transferDuration,
            });

            firebaseService.logFileTransfer({
                ext: file.name.split('.').pop() || '',
                is_sender: true, // true for Start.tsx
                is_sent: true, // true for sent
                session_id: sessionId,
                size: file.size,
                time_to_send_ms: transferDuration,
                transport: connectionType === 'server' ? 'server' : 'p2p',
            });
            firebaseService.logFirstFileTransfered(
                sessionId,
                file.name.split('.').pop() || '',
                file.size,
                transferDuration,
                connectionType === 'server',
            );

            setSendFiles((prev) =>
                prev.map((f) => (f.id === entryId ? { ...f, file: null } : f)),
            );
        } catch (err: unknown) {
            const errMsg = err instanceof Error ? err.message : String(err);
            if (errMsg === 'CANCELLED') {
                setSendErrors((prev) => ({ ...prev, [entryId]: 'cancelled' }));
            } else if (!isConnectedRef.current) {
                log('Transfer interrupted; it will restart after reconnection');
                setSendProgress((prev) => ({ ...prev, [entryId]: 0 }));
                setSendErrors((prev) => ({ ...prev, [entryId]: null }));
                void firebaseService.logEvent('file_send_retry_queued', {
                    session_id: sessionId,
                    transfer_id: transferId,
                    reason: 'connection_interrupted',
                });
            } else {
                error('Error sending file', err);
                setSendErrors((prev) => ({ ...prev, [entryId]: errMsg }));
                firebaseService.logEvent('file_sent_failure', {
                    fileExtension: file.name.split('.').pop(),
                    fileSize: file.size,
                    fileType: file.type,
                    error: errMsg,
                    transferId,
                    connectionType: await receiver.getConnectionType(),
                });
            }
        } finally {
            delete sendTransferIdToEntryIdRef.current[transferId];
            delete sendTransferMetaRef.current[entryId];
            setCurrentlySendingId(null);
        }
    };

    useEffect(() => {
        if (currentlySendingId || !isConnected || !receiverRef.current) return;
        const nextFile = sendFiles.find(
            (f) =>
                (sendProgress[f.id] || 0) < 100 &&
                !sendErrors[f.id] &&
                f.file !== null,
        );
        if (nextFile) {
            setCurrentlySendingId(nextFile.id);
            sendFileWithProgress(nextFile.file as File, nextFile.id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sendFiles, currentlySendingId, sendErrors, isConnected]);

    const handleSendFileDrop = (newFiles: File[]) => {
        const filesWithIds = newFiles.map((file) => ({
            id: Math.random().toString(36).substring(7) + '-' + Date.now(),
            file,
            name: file.name,
            size: file.size,
        }));
        setSendFiles((prev) => [...prev, ...filesWithIds]);
    };

    const handleSendRetry = (_file: File, id: string) => {
        setSendErrors((prev) => ({ ...prev, [id]: null }));
        setSendProgress((prev) => ({ ...prev, [id]: 0 }));
    };

    const handleCancelSendTransfer = (id: string) => {
        if (currentlySendingId === id && receiverRef.current) {
            let numericId = -1;
            for (const [key, val] of Object.entries(
                sendTransferIdToEntryIdRef.current,
            )) {
                if (val === id) {
                    numericId = Number(key);
                    break;
                }
            }
            if (numericId !== -1) {
                receiverRef.current.cancelTransfer(numericId);
            }
        }
    };

    if (!encryptionKey || (showQR && !uploadUrl)) {
        return (
            <div
                className="flex h-screen items-center justify-center"
                style={{ backgroundColor: '#f6f8fb' }}
            >
                <div className="flex flex-col items-center gap-4">
                    <CircularProgress size={40} />
                    <Typography className="text-gray-500 font-medium">
                        {t('start.initializingSecureSession')}
                    </Typography>
                </div>
            </div>
        );
    }

    const hasReceivingItems = Object.keys(receivingFiles).length > 0;
    const hasTransferActivity =
        isConnected || receivedFiles.length > 0 || hasReceivingItems;
    const shouldHideInitialPanel =
        hasTransferActivity && !(wasConnectedRef.current && !isConnected);
    const shouldShowSessionStatus = hasTransferActivity;
    const shouldUseCompactTransferHero = shouldHideInitialPanel;
    const heroCtaLocation =
        wasConnectedRef.current && !isConnected
            ? 'continuation_view'
            : 'initial_view';
    const shouldShowHeroButton =
        !showQR || (wasConnectedRef.current && !isConnected);
    const shouldShowFlowSection =
        !isConnected && receivedFiles.length === 0 && !hasReceivingItems;

    return (
        <div
            className="text-center font-sans relative"
            style={{ backgroundColor: '#f6f8fb' }}
        >
            <Header
                onPrivacyClick={() => setLegalType('privacy')}
                onTermsClick={() => setLegalType('tos')}
                onFaqClick={handleFaqMenuClick}
            />
            <SEO
                title={titleLabel}
                description={seoDescription}
                path={location.pathname}
            />
            <main className="fileplease-page-shell">
                <section
                    className={`fileplease-hero ${shouldUseCompactTransferHero ? 'is-transfer-active' : ''
                        }`}
                >
                    <img
                        src="/preview.jpg?v=2"
                        alt=""
                        aria-hidden="true"
                        className="fileplease-hero-art"
                    />
                    <div className="fileplease-hero-shade" />
                    <div className="fileplease-hero-inner">
                        <div className="fileplease-hero-copy">
                            <Typography
                                component="p"
                                sx={{
                                    color: '#2563EB',
                                    fontSize: { xs: '3rem', md: '4.65rem' },
                                    fontWeight: 900,
                                    lineHeight: 0.9,
                                    letterSpacing: 0,
                                }}
                            >
                                <PulsingTitle />
                            </Typography>
                            <Typography
                                component="h1"
                                sx={{
                                    color: '#0f172a',
                                    fontSize: {
                                        xs: '2rem',
                                        sm: '2.6rem',
                                        md: '3.05rem',
                                    },
                                    fontWeight: 850,
                                    lineHeight: 1.04,
                                    letterSpacing: 0,
                                    mt: 2,
                                    maxWidth: '12ch',
                                }}
                            >
                                {titleLabel}
                            </Typography>
                            <Typography
                                component="p"
                                sx={{
                                    color: '#334155',
                                    fontSize: { xs: '1rem', sm: '1.18rem' },
                                    lineHeight: 1.7,
                                    mt: 2.25,
                                    maxWidth: '34rem',
                                }}
                            >
                                {subtitleLabel}
                            </Typography>
                            <div className="fileplease-hero-actions">
                                {shouldShowHeroButton ? (
                                    <Button
                                        variant="contained"
                                        startIcon={
                                            isFakeConnecting ? (
                                                <CircularProgress
                                                    size={22}
                                                    color="inherit"
                                                />
                                            ) : (
                                                <SendIcon
                                                    sx={{
                                                        transform:
                                                            'translateY(-3px) rotate(-45deg)',
                                                    }}
                                                />
                                            )
                                        }
                                        onClick={() =>
                                            handleStartClick(heroCtaLocation)
                                        }
                                        disabled={isFakeConnecting}
                                        sx={{
                                            borderRadius: '9999px',
                                            textTransform: 'none',
                                            px: { xs: 3, sm: 4 },
                                            py: 1.55,
                                            minWidth: isFakeConnecting
                                                ? '250px'
                                                : '178px',
                                            fontSize: '1rem',
                                            fontWeight: 800,
                                            backgroundColor: '#2563EB',
                                            color: 'white',
                                            boxShadow: 'none',
                                            whiteSpace: 'nowrap',
                                            transition:
                                                'transform 180ms ease, box-shadow 180ms ease, background-color 180ms ease',
                                            '&:hover': {
                                                backgroundColor: '#1d4ed8',
                                                boxShadow:
                                                    '0 18px 40px rgba(37, 99, 235, 0.28)',
                                                transform: 'translateY(-2px)',
                                            },
                                            '&:active': {
                                                transform:
                                                    'translateY(0) scale(0.98)',
                                            },
                                            '&.Mui-disabled': {
                                                backgroundColor: '#2563EB',
                                                color: 'white',
                                                opacity: 0.9,
                                            },
                                        }}
                                    >
                                        {isFakeConnecting
                                            ? t('start.creatingConnection')
                                            : ctaLabel}
                                    </Button>
                                ) : (
                                    <a
                                        href="#fileplease-transfer-panel"
                                        className="fileplease-scan-link"
                                        aria-label={t('start.scanQrPrompt')}
                                        onClick={(event) => {
                                            event.preventDefault();
                                            if (transferPanelRef.current) {
                                                scrollToElementRespectingHeader(
                                                    transferPanelRef.current,
                                                );
                                            }
                                        }}
                                    >
                                        <PhoneIphoneIcon
                                            sx={{ fontSize: '1.05rem' }}
                                        />
                                        <span className="fileplease-scan-link-full">
                                            {t('start.scanQrPrompt')}
                                        </span>
                                        <span className="fileplease-scan-link-short">
                                            {t('start.viewQrCode')}
                                        </span>
                                    </a>
                                )}
                                <a
                                    href="#fileplease-how-it-works"
                                    className="fileplease-secondary-link"
                                >
                                    {t('start.howItWorks')}
                                </a>
                            </div>
                            <div className="fileplease-hero-proof">
                                <span>
                                    <HttpsIcon sx={{ fontSize: '1rem' }} />
                                    {t('start.how.private')}
                                </span>
                                <span>
                                    <PersonOffIcon sx={{ fontSize: '1rem' }} />
                                    {t('start.how.noAccount')}
                                </span>
                                <span>
                                    <CloudOffIcon sx={{ fontSize: '1rem' }} />
                                    {t('start.how.noStorage')}
                                </span>
                            </div>
                        </div>

                        <section
                            id="fileplease-transfer-panel"
                            ref={transferPanelRef}
                            className={`fileplease-transfer-panel ${shouldUseCompactTransferHero ? 'is-compact' : ''
                                }`}
                            aria-label="FilePlease transfer panel"
                        >
                            {hasConnected && (
                                <div
                                    className={`fileplease-panel-status-dot ${isConnected ? 'is-connected' : ''
                                        }`}
                                />
                            )}
                            <div className="fileplease-panel-header">
                                <img
                                    src="/logo192.png?v=3"
                                    alt=""
                                    aria-hidden="true"
                                    className="h-9 w-9"
                                />
                                <div>
                                    <Typography
                                        component="p"
                                        sx={{
                                            color: '#2563EB',
                                            fontWeight: 800,
                                            fontSize: '0.94rem',
                                            lineHeight: 1.25,
                                            letterSpacing: 0,
                                        }}
                                    >
                                        {t('start.privacyByDesign')}
                                    </Typography>
                                    <Typography
                                        component="p"
                                        sx={{
                                            color: '#0f172a',
                                            fontWeight: 850,
                                            fontSize: '1.15rem',
                                            lineHeight: 1.2,
                                        }}
                                    >
                                        {showQR
                                            ? t('start.scanQrPrompt')
                                            : ctaLabel}
                                    </Typography>
                                </div>
                            </div>
                            <TransferFallback signaling={activeSignaling} />
                            <div
                                className={`fileplease-panel-body ${shouldHideInitialPanel ? 'is-collapsed' : ''
                                    }`}
                            >
                                {wasConnectedRef.current && !isConnected ? (
                                    <div className="fileplease-panel-state">
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                color: '#1f2937',
                                                fontWeight: 700,
                                                lineHeight: 1.5,
                                            }}
                                        >
                                            {t('start.sessionOver')}
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            startIcon={
                                                isFakeConnecting ? (
                                                    <CircularProgress
                                                        size={20}
                                                        color="inherit"
                                                    />
                                                ) : (
                                                    <SendIcon
                                                        sx={{
                                                            transform:
                                                                'translateY(-3px) rotate(-45deg)',
                                                        }}
                                                    />
                                                )
                                            }
                                            onClick={() =>
                                                handleStartClick(
                                                    'continuation_view',
                                                )
                                            }
                                            disabled={isFakeConnecting}
                                            sx={{
                                                borderRadius: '9999px',
                                                textTransform: 'none',
                                                py: 1.4,
                                                px: 3.5,
                                                mt: 2.5,
                                                minWidth: isFakeConnecting
                                                    ? '240px'
                                                    : '160px',
                                                fontWeight: 800,
                                                backgroundColor: '#2563EB',
                                                color: 'white',
                                                boxShadow: 'none',
                                                '&:hover': {
                                                    backgroundColor: '#1d4ed8',
                                                    boxShadow:
                                                        '0 14px 32px rgba(37, 99, 235, 0.26)',
                                                },
                                                '&.Mui-disabled': {
                                                    backgroundColor: '#2563EB',
                                                    color: 'white',
                                                    opacity: 0.9,
                                                },
                                            }}
                                        >
                                            {isFakeConnecting
                                                ? t('start.creatingConnection')
                                                : ctaLabel}
                                        </Button>
                                    </div>
                                ) : !showQR ? (
                                    <div className="fileplease-panel-state">
                                        <div className="fileplease-ready-visual">
                                            <img
                                                src="/how-to-step3.png?v=2"
                                                alt="Phone sending files to a computer"
                                            />
                                        </div>
                                        <Button
                                            variant="contained"
                                            startIcon={
                                                isFakeConnecting ? (
                                                    <CircularProgress
                                                        size={24}
                                                        color="inherit"
                                                    />
                                                ) : (
                                                    <SendIcon
                                                        sx={{
                                                            transform:
                                                                'translateY(-3px) rotate(-45deg)',
                                                        }}
                                                    />
                                                )
                                            }
                                            onClick={() =>
                                                handleStartClick('initial_view')
                                            }
                                            disabled={isFakeConnecting}
                                            sx={{
                                                borderRadius: '9999px',
                                                textTransform: 'none',
                                                py: 1.6,
                                                px: { xs: 3.5, sm: 5 },
                                                mt: 3,
                                                minWidth: isFakeConnecting
                                                    ? '280px'
                                                    : '200px',
                                                fontSize: {
                                                    xs: '1rem',
                                                    sm: '1.08rem',
                                                },
                                                whiteSpace: 'nowrap',
                                                fontWeight: 800,
                                                backgroundColor: '#2563EB',
                                                color: 'white',
                                                boxShadow: 'none',
                                                transition:
                                                    'transform 180ms ease, box-shadow 180ms ease, background-color 180ms ease',
                                                '&:hover': {
                                                    backgroundColor: '#1d4ed8',
                                                    boxShadow:
                                                        '0 18px 40px rgba(37, 99, 235, 0.28)',
                                                    transform:
                                                        'translateY(-2px)',
                                                },
                                                '&:active': {
                                                    transform:
                                                        'translateY(0) scale(0.98)',
                                                },
                                                '&.Mui-disabled': {
                                                    backgroundColor: '#2563EB',
                                                    color: 'white',
                                                    opacity: 0.9,
                                                },
                                            }}
                                        >
                                            {isFakeConnecting
                                                ? t('start.creatingConnection')
                                                : ctaLabel}
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="fileplease-panel-state">
                                        {shouldRenderQrCode ? (
                                            renderQRCode(uploadUrl)
                                        ) : (
                                            <div className="my-[20px] min-h-[210px] flex items-center justify-center">
                                                <CircularProgress size={40} />
                                            </div>
                                        )}
                                        <UrlSection url={uploadUrl} />
                                        <div className="mt-3 flex flex-col items-center min-h-[24px]">
                                            {isConnected ? (
                                                <div className="flex items-center gap-1.5 animate-fade-in">
                                                    <DoneIcon
                                                        sx={{
                                                            fontSize: '1.2rem',
                                                            color: '#16a34a',
                                                        }}
                                                    />
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: '#16a34a',
                                                            fontWeight: 'bold',
                                                        }}
                                                    >
                                                        {t('start.connected')}
                                                    </Typography>
                                                </div>
                                            ) : isConnecting ? (
                                                <div className="flex items-center gap-2 animate-fade-in">
                                                    <CircularProgress
                                                        size={14}
                                                        sx={{
                                                            color: '#64748b',
                                                        }}
                                                    />
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: '#64748b',
                                                            fontWeight: 700,
                                                        }}
                                                    >
                                                        {t(
                                                            'start.senderConnecting',
                                                        )}
                                                    </Typography>
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div
                                className={`fileplease-session-status ${shouldShowSessionStatus ? 'is-visible' : ''
                                    }`}
                            >
                                {receivedFiles.length > 0 ? (
                                    <Typography
                                        variant="body1"
                                        className="text-green-700 font-bold flex items-center justify-center gap-1"
                                    >
                                        <DoneIcon sx={{ fontSize: '1.5rem' }} />{' '}
                                        {t('start.filesReceived')}
                                    </Typography>
                                ) : hasReceivingItems ? (
                                    <Typography
                                        variant="body1"
                                        className="text-green-700 font-bold flex items-center justify-center gap-2"
                                    >
                                        <CircularProgress
                                            size={20}
                                            color="inherit"
                                        />{' '}
                                        {t('start.receivingFiles')}
                                    </Typography>
                                ) : isConnected ? (
                                    <Typography
                                        variant="body1"
                                        className="text-green-700 font-bold flex items-center justify-center gap-1"
                                    >
                                        <DoneIcon sx={{ fontSize: '1.5rem' }} />{' '}
                                        {t('start.connectedSendReceive')}
                                    </Typography>
                                ) : null}
                            </div>
                            <div className="fileplease-panel-trust">
                                <span>
                                    <LockIcon sx={{ fontSize: '0.95rem' }} />
                                    {t('start.how.private')}
                                </span>
                                <span>{t('start.how.noStorage')}</span>
                            </div>
                            {sessionId && (
                                <div className="fileplease-session-id">
                                    <Typography variant="body2">
                                        {t('common.sessionId')} {sessionId}
                                    </Typography>
                                </div>
                            )}
                        </section>
                    </div>
                </section>

                <div className="fileplease-transfer-stack">
                    {renderReceivingFiles(receivingFiles, t)}
                    {renderReceivedFiles(
                        receivedFiles,
                        receivedFilesRef,
                        (file, url) => {
                            setPreviewFile(file);
                            setPreviewUrl(url);
                        },
                        feedbackFilesCount,
                        sessionId,
                        t,
                    )}
                </div>
                {shouldShowFlowSection &&
                    renderHowItWorksList(t, locale, howToStartOptions)}
                {isConnected && (
                    <Card
                        className="w-full max-w-2xl mx-auto m-3 relative"
                        variant="outlined"
                        sx={{
                            borderRadius: '20px',
                            backgroundColor: '#ffffff',
                            borderColor: '#ffffff',
                            borderWidth: '1px',
                        }}
                    >
                        <CardContent>
                            <Typography variant="h6">
                                {t('start.sendFiles')}
                            </Typography>
                            <Typography
                                variant="body2"
                                className="text-gray-600 mt-1"
                            >
                                {t('start.sendFilesHint')}
                            </Typography>
                            <Divider sx={{ my: 2 }} />
                            <div className="flex justify-center mt-2 mb-6">
                                <input
                                    type="file"
                                    id="start-send-file-input"
                                    style={{ display: 'none' }}
                                    multiple
                                    onChange={(e) => {
                                        if (
                                            e.target.files &&
                                            e.target.files.length > 0
                                        ) {
                                            handleSendFileDrop(
                                                Array.from(e.target.files),
                                            );
                                            e.target.value = '';
                                        }
                                    }}
                                />
                                <label htmlFor="start-send-file-input">
                                    <Button
                                        variant="outlined"
                                        component="span"
                                        color="primary"
                                        sx={{ paddingX: '16px' }}
                                    >
                                        {t('start.selectFilesToSend')}
                                    </Button>
                                </label>
                            </div>
                            {renderSendFiles(
                                sendFiles,
                                sendProgress,
                                sendErrors,
                                (file) => {
                                    setPreviewFile(file);
                                    setPreviewUrl(null);
                                },
                                handleSendRetry,
                                sendSpeeds,
                                isConnected,
                                currentlySendingId,
                                handleCancelSendTransfer,
                            )}
                            {completedReceivedFilesCount === 0 &&
                                completedSentFilesCount > 0 && (
                                    <FeedbackSection
                                        filesCount={feedbackFilesCount}
                                        isSender={true}
                                        sessionId={sessionId}
                                    />
                                )}
                        </CardContent>
                    </Card>
                )}
                {shouldRenderSeoContent && (
                    <SeoContentSection
                        locale={locale}
                        pathname={location.pathname}
                        t={t}
                        showLongFormCard={!isConnected}
                        faqCardRef={faqCardRef}
                        longFormCtaOptions={howToStartOptions}
                    />
                )}
                <footer className="mt-4 mb-4 flex flex-wrap justify-center gap-6 px-4">
                    <Typography
                        variant="caption"
                        className="text-gray-400 hover:text-gray-600 cursor-pointer transition-colors"
                        onClick={() => setLegalType('privacy')}
                    >
                        {t('menu.privacyPolicy')}
                    </Typography>
                    <Typography
                        variant="caption"
                        className="text-gray-400 hover:text-gray-600 cursor-pointer transition-colors"
                        onClick={() => setLegalType('tos')}
                    >
                        {t('menu.termsOfService')}
                    </Typography>
                    <Typography
                        variant="caption"
                        component="a"
                        href={SUPPORT_MAILTO}
                        className="text-gray-400 hover:text-gray-600 cursor-pointer transition-colors no-underline"
                        onClick={handleSupportClick}
                    >
                        {t('menu.contactSupport')}
                    </Typography>
                    <Typography variant="caption" className="text-gray-400">
                        {t('footer.copyright')}
                    </Typography>
                </footer>
            </main>
            <LegalDialog
                open={legalType !== null}
                type={legalType}
                onClose={() => setLegalType(null)}
            />
            <ImagePreviewModal
                file={previewFile}
                previewUrl={previewUrl}
                onClose={() => {
                    setPreviewFile(null);
                    setPreviewUrl(null);
                }}
            />
            <Dialog
                open={showP2PBlockedModal}
                onClose={() => setShowP2PBlockedModal(false)}
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        padding: '8px',
                    },
                }}
            >
                <DialogTitle className="font-bold text-gray-800">
                    {t('dialog.p2pBlockedTitle')}
                </DialogTitle>
                <DialogContent>
                    <Typography className="text-gray-600">
                        {t('dialog.p2pBlockedMessage')}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
                    <Button
                        onClick={() => setShowP2PBlockedModal(false)}
                        variant="contained"
                        sx={{
                            borderRadius: '4px',
                            textTransform: 'none',
                            px: 4,
                            backgroundColor: '#1976d2',
                            '&:hover': {
                                backgroundColor: '#115293',
                            },
                        }}
                    >
                        {t('dialog.okGotIt')}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={showClosedModal}
                onClose={() => setShowClosedModal(false)}
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        padding: '8px',
                    },
                }}
            >
                <DialogTitle className="font-bold text-gray-800">
                    {t('dialog.connectionClosedTitle')}
                </DialogTitle>
                <DialogContent>
                    <Typography className="text-gray-600">
                        {t('dialog.connectionClosedMessageStart')}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button
                        onClick={() => {
                            setShowClosedModal(false);
                            setClosedModalDismissed(true);
                        }}
                        variant="text"
                        sx={{
                            borderRadius: '4px',
                            textTransform: 'none',
                            color: 'text.secondary',
                        }}
                    >
                        {t('dialog.close')}
                    </Button>
                    <Button
                        onClick={() => {
                            setShowClosedModal(false);
                            setClosedModalDismissed(true);
                        }}
                        variant="contained"
                        sx={{
                            borderRadius: '4px',
                            textTransform: 'none',
                            px: 3,
                            backgroundColor: '#1976d2',
                            '&:hover': {
                                backgroundColor: '#115293',
                            },
                        }}
                    >
                        {t('dialog.newSession')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={showTimeoutModal}
                onClose={() => setShowTimeoutModal(false)}
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        padding: '8px',
                    },
                }}
            >
                <DialogTitle className="font-bold text-gray-800">
                    {t('dialog.sessionTimedOutTitle')}
                </DialogTitle>
                <DialogContent>
                    <Typography className="text-gray-600">
                        {t('dialog.sessionTimedOutMessage')}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button
                        onClick={() => setShowTimeoutModal(false)}
                        variant="text"
                        sx={{
                            borderRadius: '4px',
                            textTransform: 'none',
                            color: 'text.secondary',
                        }}
                    >
                        {t('action.cancel')}
                    </Button>
                    <Button
                        onClick={handleStartNewSession}
                        variant="contained"
                        sx={{
                            borderRadius: '4px',
                            textTransform: 'none',
                            px: 3,
                            backgroundColor: '#1976d2',
                            '&:hover': {
                                backgroundColor: '#115293',
                            },
                        }}
                    >
                        {t('dialog.startNewSession')}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default Start;
